import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { urlBase64ToUint8Array } from '../utils/helpers';

export const useNotifications = (getUserId, mandanti, rebelnetCampaigns = [], country = 'IT') => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isDeviceRegistered, setIsDeviceRegistered] = useState(false);
  const [countryPreferences, setCountryPreferences] = useState({ IT: [], ES: [] });
  const [myDevices, setMyDevices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  // Derive flat preferences for the currently selected country (backward-compatible interface)
  const preferences = countryPreferences[country] || [];

  const setPreferences = useCallback((newPrefs) => {
    if (Array.isArray(newPrefs)) {
      // Backward-compatible: set preferences for the current country
      setCountryPreferences(prev => ({ ...prev, [country]: newPrefs }));
    } else {
      // Direct countryPreferences object
      setCountryPreferences(newPrefs);
    }
  }, [country]);

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
        return;
      }
      
      if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          setNotificationsEnabled(true);
          registerPushSubscription();
        } else {
          setNotificationsEnabled(false);
        }
      }
    }
  };

  const registerPushSubscription = async () => {
    try {
      const userId = getUserId();
      
      if (userId === 'anonymous') {
        alert('Devi essere autenticato per registrare il dispositivo');
        return;
      }
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY)
      });

      const deviceInfo = `${navigator.platform} - ${navigator.userAgent.match(/\(([^)]+)\)/)?.[1] || 'Unknown'}`;

      await api.subscribe({
        userId,
        deviceToken: JSON.stringify(subscription),
        enabledMandanti: preferences,
        countryPreferences,
        deviceInfo
      });
      
      setIsDeviceRegistered(true);
      alert('Dispositivo registrato con successo!');
      loadMyDevices();
    } catch (error) {
      console.error('Push subscription error:', error);
    }
  };

  const toggleMandantePreference = async (mandante) => {
    const currentPrefs = countryPreferences[country] || [];
    const newCountryPrefs = currentPrefs.includes(mandante)
      ? currentPrefs.filter(m => m !== mandante)
      : [...currentPrefs, mandante];
    
    const newCountryPreferences = { ...countryPreferences, [country]: newCountryPrefs };
    setCountryPreferences(newCountryPreferences);

    const userId = getUserId();

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const deviceId = btoa(subscription.endpoint).substring(0, 50);
          
          await api.updatePreferences({
            userId,
            deviceId,
            enabledMandanti: newCountryPrefs,
            countryPreferences: newCountryPreferences
          });
          loadMyDevices();
        }
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const loadMyDevices = useCallback(async () => {
    try {
      const userId = getUserId();
      const data = await api.getSubscriptions();
      const userDevices = (data.items || []).filter(s => s.userId === userId);
      
      if ('serviceWorker' in navigator && userDevices.length > 0) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            const currentDeviceId = btoa(subscription.endpoint).substring(0, 50);
            userDevices.forEach(d => {
              d.isCurrentDevice = d.deviceId === currentDeviceId;
            });
            const isRegistered = userDevices.some(d => d.deviceId === currentDeviceId);
            setIsDeviceRegistered(isRegistered);
            
            const currentDevice = userDevices.find(d => d.deviceId === currentDeviceId);
            if (currentDevice) {
              if (currentDevice.countryPreferences) {
                // New format: use countryPreferences directly
                setCountryPreferences(currentDevice.countryPreferences);
              } else if (currentDevice.enabledMandanti) {
                // Backward compatibility: treat enabledMandanti as IT preferences
                setCountryPreferences(prev => ({
                  ...prev,
                  IT: currentDevice.enabledMandanti
                }));
              }
            }
          }
        } catch (err) {
          console.error('Error checking device registration:', err);
        }
      }
      
      setMyDevices(userDevices);
    } catch (error) {
      console.error('Error loading my devices:', error);
    }
  }, [getUserId]);

  const loadSubscriptions = async () => {
    try {
      const data = await api.getSubscriptions();
      setSubscriptions(data.items || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const toggleAllNotifications = async (userId, deviceId, enable) => {
    try {
      const allNotifications = enable ? [...mandanti, ...rebelnetCampaigns, 'OPERATIONAL'] : [];
      const newCountryPreferences = { ...countryPreferences, [country]: allNotifications };
      await api.updatePreferences({
        userId,
        deviceId,
        enabledMandanti: allNotifications,
        countryPreferences: newCountryPreferences
      });
      loadSubscriptions();
    } catch (error) {
      console.error('Error updating all notifications:', error);
    }
  };

  const deleteSubscription = async (userId, deviceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo dispositivo?')) return;
    
    try {
      await api.deleteSubscription(userId, deviceId);
      loadSubscriptions();
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  return {
    notificationsEnabled,
    isDeviceRegistered,
    preferences,
    countryPreferences,
    myDevices,
    subscriptions,
    requestNotificationPermission,
    registerPushSubscription,
    toggleMandantePreference,
    loadMyDevices,
    loadSubscriptions,
    toggleAllNotifications,
    deleteSubscription,
    setPreferences,
    setCountryPreferences
  };
};
